import { getCargoMeta } from './painel-meta.js';
import {
  extractSecaoFromKey,
  extractZonaFromKey,
  getCandidateFederation,
  getCandidateParty,
  getCandidateRef,
  getCargoLegendVotes,
  getOfficialCargoSummary,
  toOptionalNumber,
} from './painel-data.js';

export { buildElectionPairOptions } from './painel-temporal.js';

export function buildComparativoData(dados, cargoKey, selectedIds = []) {
  const secoesObj = dados?.secoes || dados || {};
  const candidatosRef = dados?.candidatos || {};
  const officialCargoSummary = getOfficialCargoSummary(dados, cargoKey);
  const selectedSet = new Set(selectedIds);
  const allCandidates = new Map();
  const zonasMap = new Map();
  const secoes = [];
  let totalCargoVotos = 0;
  let totalVotosNominais = 0;
  let totalVotosLegenda = 0;
  let votosLegendaDisponiveis = false;
  let totalSecoes = 0;

  Object.entries(secoesObj).forEach(([key, sec]) => {
    const lista = sec?.[`raiox_${cargoKey}`];
    if (!Array.isArray(lista)) return;

    totalSecoes += 1;
    const votosLegendaSecao = getCargoLegendVotes(sec, cargoKey);
    if (votosLegendaSecao !== null) {
      votosLegendaDisponiveis = true;
      totalVotosLegenda += votosLegendaSecao;
    }
    const zona = String(sec.zona || extractZonaFromKey(key) || 'N/D');
    const secao = String(sec.secao || extractSecaoFromKey(key) || key);
    const votosSecao = {};

    lista.forEach(item => {
      const numero = String(item.numero);
      const votos = Number(item.votos || 0);
      const id = `${cargoKey}_${numero}`;
      const candidate = ensureComparativoCandidate(allCandidates, candidatosRef, cargoKey, numero);
      candidate.votos += votos;
      totalVotosNominais += votos;

      if (!selectedSet.has(id)) return;
      votosSecao[id] = (votosSecao[id] || 0) + votos;

      if (!zonasMap.has(zona)) zonasMap.set(zona, { zona, votosPorCandidato: {}, totalSelecionado: 0 });
      const zonaData = zonasMap.get(zona);
      zonaData.votosPorCandidato[id] = (zonaData.votosPorCandidato[id] || 0) + votos;
      zonaData.totalSelecionado += votos;
    });

    if (Object.keys(votosSecao).length > 0) {
      secoes.push({ key, zona, secao, votosPorCandidato: votosSecao });
    }
  });

  const officialNominais = toOptionalNumber(officialCargoSummary?.votos_nominais);
  if (officialNominais !== null) totalVotosNominais = officialNominais;

  const officialLegenda = toOptionalNumber(officialCargoSummary?.votos_legenda);
  if (officialLegenda !== null) {
    votosLegendaDisponiveis = true;
    totalVotosLegenda = officialLegenda;
  }

  const officialValidos = toOptionalNumber(officialCargoSummary?.votos_validos);
  totalCargoVotos = officialValidos !== null
    ? officialValidos
    : totalVotosNominais + (votosLegendaDisponiveis ? totalVotosLegenda : 0);

  const candidatos = Array.from(allCandidates.values())
    .map(candidate => ({ ...candidate, percentual: totalCargoVotos > 0 ? (candidate.votos / totalCargoVotos) * 100 : 0 }))
    .sort((a, b) => b.votos - a.votos || a.nome.localeCompare(b.nome, 'pt-BR'));

  const rankById = new Map(candidatos.map((candidate, index) => [candidate.id, index + 1]));
  const selecionados = selectedIds
    .map(id => candidatos.find(candidate => candidate.id === id))
    .filter(Boolean)
    .sort((a, b) => b.votos - a.votos || a.nome.localeCompare(b.nome, 'pt-BR'))
    .map(candidate => ({
      ...candidate,
      rank: rankById.get(candidate.id) || 0,
      zonasVencidas: 0,
      secoesVencidas: 0,
      diffToLeader: 0,
    }));

  const selectedById = new Map(selecionados.map(candidate => [candidate.id, candidate]));
  const zonas = Array.from(zonasMap.values()).map(row => enrichComparativoRow(row, selectedById));
  zonas.forEach(zona => {
    if (zona.lider && selectedById.has(zona.lider.id)) selectedById.get(zona.lider.id).zonasVencidas += 1;
  });

  const secoesEnriched = secoes.map(secao => enrichComparativoRow(secao, selectedById))
    .sort((a, b) => b.diferenca - a.diferenca || b.totalSelecionado - a.totalSelecionado);
  secoesEnriched.forEach(secao => {
    if (secao.lider && selectedById.has(secao.lider.id)) selectedById.get(secao.lider.id).secoesVencidas += 1;
  });

  const leaderVotes = selecionados[0]?.votos || 0;
  selecionados.forEach(candidate => {
    candidate.diffToLeader = Math.max(0, leaderVotes - candidate.votos);
  });

  return {
    cargoKey,
    cargo: getCargoMeta(cargoKey),
    candidatos,
    selecionados,
    totalCargoVotos,
    totalVotosNominais,
    totalVotosLegenda: votosLegendaDisponiveis ? totalVotosLegenda : null,
    votosLegendaDisponiveis,
    totalSecoes,
    zonas: zonas.sort((a, b) => Number(a.zona) - Number(b.zona) || String(a.zona).localeCompare(String(b.zona), 'pt-BR')),
    secoes: secoesEnriched,
    diferencaPrimeiroSegundo: Math.max(0, (selecionados[0]?.votos || 0) - (selecionados[1]?.votos || 0)),
  };
}

function ensureComparativoCandidate(map, candidatosRef, cargoKey, numero) {
  const id = `${cargoKey}_${numero}`;
  if (!map.has(id)) {
    const meta = getCargoMeta(cargoKey);
    const ref = getCandidateRef(candidatosRef, cargoKey, numero);
    map.set(id, {
      id,
      numero,
      cargoKey,
      cargo: meta.singular,
      cargoLabel: meta.label,
      nome: ref.nome || `Candidato ${numero}`,
      partido: getCandidateParty(ref),
      federacao: getCandidateFederation(ref),
      votos: 0,
    });
  }
  return map.get(id);
}

function enrichComparativoRow(row, selectedById) {
  const ordered = Object.entries(row.votosPorCandidato)
    .map(([id, votos]) => ({ ...(selectedById.get(id) || { id, nome: id }), votos }))
    .sort((a, b) => b.votos - a.votos || a.nome.localeCompare(b.nome, 'pt-BR'));

  return {
    ...row,
    totalSelecionado: ordered.reduce((sum, candidate) => sum + candidate.votos, 0),
    lider: ordered[0] || null,
    segundo: ordered[1] || null,
    diferenca: Math.max(0, (ordered[0]?.votos || 0) - (ordered[1]?.votos || 0)),
  };
}
